import React, { useState, useMemo } from 'react';
import { 
  ChevronDown, 
  ChevronRight, 
  CheckCircle2, 
  Circle, 
  XCircle,
  MinusCircle,
  Layers,
  Filter
} from 'lucide-react';

// Domain icons and colors mapping
const DOMAIN_CONFIG = {
  // CMMC / NIST domains
  'Access Control': { abbr: 'AC', color: 'violet', icon: '🔐' },
  'Awareness and Training': { abbr: 'AT', color: 'blue', icon: '📚' },
  'Audit and Accountability': { abbr: 'AU', color: 'cyan', icon: '📋' },
  'Configuration Management': { abbr: 'CM', color: 'emerald', icon: '⚙️' },
  'Identification and Authentication': { abbr: 'IA', color: 'amber', icon: '🪪' },
  'Incident Response': { abbr: 'IR', color: 'red', icon: '🚨' },
  'Maintenance': { abbr: 'MA', color: 'orange', icon: '🔧' },
  'Media Protection': { abbr: 'MP', color: 'purple', icon: '💾' },
  'Personnel Security': { abbr: 'PS', color: 'pink', icon: '👥' },
  'Physical Protection': { abbr: 'PE', color: 'teal', icon: '🏢' },
  'Risk Assessment': { abbr: 'RA', color: 'rose', icon: '📊' },
  'Security Assessment': { abbr: 'CA', color: 'indigo', icon: '🔍' },
  'System and Communications Protection': { abbr: 'SC', color: 'sky', icon: '🛡️' },
  'System and Information Integrity': { abbr: 'SI', color: 'lime', icon: '✅' },
  // HIPAA categories
  'Administrative Safeguards': { abbr: 'AS', color: 'blue', icon: '📁' },
  'Physical Safeguards': { abbr: 'PS', color: 'emerald', icon: '🏢' },
  'Technical Safeguards': { abbr: 'TS', color: 'violet', icon: '💻' },
  'Organizational Requirements': { abbr: 'OR', color: 'amber', icon: '🏛️' },
  'Policies and Procedures': { abbr: 'PP', color: 'cyan', icon: '📜' },
  // Default
  'default': { abbr: '??', color: 'slate', icon: '📄' }
};

const getColorClasses = (color, isActive) => {
  const colors = {
    violet: isActive ? 'bg-violet-500/20 border-violet-500/50 text-violet-300' : 'hover:bg-violet-500/10 border-transparent',
    blue: isActive ? 'bg-blue-500/20 border-blue-500/50 text-blue-300' : 'hover:bg-blue-500/10 border-transparent',
    cyan: isActive ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300' : 'hover:bg-cyan-500/10 border-transparent',
    emerald: isActive ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300' : 'hover:bg-emerald-500/10 border-transparent',
    amber: isActive ? 'bg-amber-500/20 border-amber-500/50 text-amber-300' : 'hover:bg-amber-500/10 border-transparent',
    red: isActive ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'hover:bg-red-500/10 border-transparent',
    orange: isActive ? 'bg-orange-500/20 border-orange-500/50 text-orange-300' : 'hover:bg-orange-500/10 border-transparent',
    purple: isActive ? 'bg-purple-500/20 border-purple-500/50 text-purple-300' : 'hover:bg-purple-500/10 border-transparent',
    pink: isActive ? 'bg-pink-500/20 border-pink-500/50 text-pink-300' : 'hover:bg-pink-500/10 border-transparent',
    teal: isActive ? 'bg-teal-500/20 border-teal-500/50 text-teal-300' : 'hover:bg-teal-500/10 border-transparent',
    rose: isActive ? 'bg-rose-500/20 border-rose-500/50 text-rose-300' : 'hover:bg-rose-500/10 border-transparent',
    indigo: isActive ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-300' : 'hover:bg-indigo-500/10 border-transparent',
    sky: isActive ? 'bg-sky-500/20 border-sky-500/50 text-sky-300' : 'hover:bg-sky-500/10 border-transparent',
    lime: isActive ? 'bg-lime-500/20 border-lime-500/50 text-lime-300' : 'hover:bg-lime-500/10 border-transparent',
    slate: isActive ? 'bg-slate-500/20 border-slate-500/50 text-slate-300' : 'hover:bg-slate-500/10 border-transparent',
  };
  return colors[color] || colors.slate;
};

const getProgressColor = (color) => {
  const colors = {
    violet: 'bg-violet-500',
    blue: 'bg-blue-500',
    cyan: 'bg-cyan-500',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    orange: 'bg-orange-500',
    purple: 'bg-purple-500',
    pink: 'bg-pink-500',
    teal: 'bg-teal-500',
    rose: 'bg-rose-500',
    indigo: 'bg-indigo-500',
    sky: 'bg-sky-500',
    lime: 'bg-lime-500',
    slate: 'bg-slate-500',
  };
  return colors[color] || colors.slate;
};

const DomainNavigator = ({ 
  questions, 
  currentQuestionId,
  selectedDomain, 
  onSelectDomain, 
  onSelectQuestion,
  collapsed = false 
}) => {
  const [expandedDomains, setExpandedDomains] = useState({});
  const [showAllQuestions, setShowAllQuestions] = useState(false);

  // Calculate domain statistics
  const domainStats = useMemo(() => {
    const stats = {};
    
    questions.forEach(q => {
      const domain = q.category || q.domain || 'Uncategorized';
      
      if (!stats[domain]) {
        stats[domain] = {
          total: 0,
          answered: 0,
          yes: 0,
          no: 0,
          na: 0,
          unanswered: 0,
          questions: []
        };
      }
      
      stats[domain].total++;
      stats[domain].questions.push(q);
      
      const answer = q.answer || 'N';  // Default is now 'No' - must earn Yes
      if (answer === 'Y') {
        stats[domain].answered++;
        stats[domain].yes++;
      } else if (answer === 'N') {
        stats[domain].answered++;
        stats[domain].no++;
      } else if (answer === 'NA') {
        stats[domain].answered++;
        stats[domain].na++;
      } else {
        stats[domain].unanswered++;
      }
    });
    
    // Sort domains by their order in questions
    const orderedDomains = [];
    const seenDomains = new Set();
    questions.forEach(q => {
      const domain = q.category || q.domain || 'Uncategorized';
      if (!seenDomains.has(domain)) {
        seenDomains.add(domain);
        orderedDomains.push(domain);
      }
    });
    
    return { stats, orderedDomains };
  }, [questions]);

  const toggleDomainExpand = (domain) => {
    setExpandedDomains(prev => ({
      ...prev,
      [domain]: !prev[domain]
    }));
  };

  const handleDomainClick = (domain) => {
    if (selectedDomain === domain) {
      onSelectDomain(null); // Clear filter
    } else {
      onSelectDomain(domain);
    }
  };

  const getDomainConfig = (domain) => {
    return DOMAIN_CONFIG[domain] || DOMAIN_CONFIG['default'];
  };

  // Calculate overall progress
  const totalAnswered = questions.filter(q => q.answer && q.answer !== 'U').length;
  const totalQuestions = questions.length;
  const overallProgress = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

  return (
    <div 
      data-testid="domain-navigator" 
      className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-violet-400" />
            <h3 className="text-lg font-semibold text-white">Domains</h3>
          </div>
          {selectedDomain && (
            <button
              onClick={() => onSelectDomain(null)}
              className="text-xs px-2 py-1 bg-violet-500/20 text-violet-300 rounded-md hover:bg-violet-500/30 transition-colors flex items-center gap-1"
            >
              <Filter className="w-3 h-3" />
              Clear Filter
            </button>
          )}
        </div>
        
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">Overall Progress</span>
            <span className="text-white font-medium">{totalAnswered}/{totalQuestions} ({overallProgress}%)</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-500"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Domain List */}
      <div className="max-h-[500px] overflow-y-auto">
        {domainStats.orderedDomains.map((domain) => {
          const stats = domainStats.stats[domain];
          const config = getDomainConfig(domain);
          const isActive = selectedDomain === domain;
          const isExpanded = expandedDomains[domain];
          const completionPercent = stats.total > 0 ? Math.round((stats.answered / stats.total) * 100) : 0;
          
          return (
            <div key={domain} className="border-b border-slate-700/50 last:border-b-0">
              {/* Domain Header */}
              <div
                data-testid={`domain-${domain.toLowerCase().replace(/\s+/g, '-')}`}
                className={`p-3 cursor-pointer transition-all border-l-4 ${getColorClasses(config.color, isActive)} ${
                  isActive ? 'border-l-4' : 'border-l-transparent'
                }`}
              >
                <div className="flex items-center gap-2" onClick={() => handleDomainClick(domain)}>
                  {/* Expand/Collapse Toggle */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleDomainExpand(domain); }}
                    className="p-0.5 hover:bg-slate-700 rounded transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                  
                  {/* Domain Icon & Name */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{config.icon}</span>
                      <span className={`font-medium text-sm truncate ${isActive ? '' : 'text-slate-200'}`}>
                        {domain}
                      </span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getProgressColor(config.color)} rounded-full transition-all duration-500`}
                          style={{ width: `${completionPercent}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {stats.answered}/{stats.total}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Quick Stats Row */}
                <div className="flex items-center gap-3 mt-2 ml-7 text-xs">
                  <span className="flex items-center gap-1 text-green-400">
                    <CheckCircle2 className="w-3 h-3" />
                    {stats.yes}
                  </span>
                  <span className="flex items-center gap-1 text-red-400">
                    <XCircle className="w-3 h-3" />
                    {stats.no}
                  </span>
                  <span className="flex items-center gap-1 text-gray-400">
                    <MinusCircle className="w-3 h-3" />
                    {stats.na}
                  </span>
                  <span className="flex items-center gap-1 text-slate-500">
                    <Circle className="w-3 h-3" />
                    {stats.unanswered}
                  </span>
                </div>
              </div>
              
              {/* Expanded Question List */}
              {isExpanded && (
                <div className="bg-slate-900/50 border-t border-slate-700/50">
                  {stats.questions.slice(0, showAllQuestions ? undefined : 10).map((q, idx) => {
                    const isCurrentQuestion = q.questionId === currentQuestionId;
                    const answerStatus = q.answer || 'U';
                    
                    return (
                      <button
                        key={q.questionId}
                        onClick={() => onSelectQuestion(q.questionId)}
                        className={`w-full p-2 pl-10 text-left text-xs border-l-2 transition-colors ${
                          isCurrentQuestion 
                            ? 'bg-violet-500/20 border-l-violet-500 text-white' 
                            : 'border-l-transparent hover:bg-slate-800 text-slate-400 hover:text-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {/* Answer Status Indicator */}
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            answerStatus === 'Y' ? 'bg-green-500' :
                            answerStatus === 'N' ? 'bg-red-500' :
                            answerStatus === 'NA' ? 'bg-gray-500' :
                            'bg-slate-600'
                          }`} />
                          
                          {/* Question ID */}
                          <span className="font-mono text-slate-500 flex-shrink-0">
                            {q.questionId || `Q${idx + 1}`}
                          </span>
                          
                          {/* Question Text (truncated) */}
                          <span className="truncate">
                            {q.questionText?.substring(0, 50)}...
                          </span>
                        </div>
                      </button>
                    );
                  })}
                  
                  {/* Show More Button */}
                  {stats.questions.length > 10 && !showAllQuestions && (
                    <button
                      onClick={() => setShowAllQuestions(true)}
                      className="w-full p-2 pl-10 text-xs text-violet-400 hover:bg-slate-800 transition-colors"
                    >
                      Show {stats.questions.length - 10} more questions...
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Footer Summary */}
      <div className="p-3 bg-slate-900/50 border-t border-slate-700">
        <div className="grid grid-cols-4 gap-2 text-center text-xs">
          <div>
            <div className="text-green-400 font-semibold">{questions.filter(q => q.answer === 'Y').length}</div>
            <div className="text-slate-500">Yes</div>
          </div>
          <div>
            <div className="text-red-400 font-semibold">{questions.filter(q => q.answer === 'N').length}</div>
            <div className="text-slate-500">No</div>
          </div>
          <div>
            <div className="text-gray-400 font-semibold">{questions.filter(q => q.answer === 'NA').length}</div>
            <div className="text-slate-500">N/A</div>
          </div>
          <div>
            <div className="text-slate-400 font-semibold">{questions.filter(q => !q.answer || q.answer === 'U').length}</div>
            <div className="text-slate-500">Pending</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DomainNavigator;
